import { giveawayUrl } from './slug'

/**
 * Referral links.
 *
 * A giveaway with a referral entry option gives each person a link of their
 * own: the giveaway's address with `?ref={their user id}`. Whoever opens it
 * and then enters earns them that option's entries.
 *
 * The referrer is remembered per giveaway in local storage, because the
 * visitor usually has to sign up or sign in first, and that can leave the page
 * (Discord OAuth) before they get to enter.
 */

export const REFERRAL_PARAM = 'ref'

const keyFor = (giveawayId: number) => `jlm:referral:${giveawayId}`

/** The link that credits `userId` for people who enter through it. */
export function referralUrl(slug: string, userId: number, origin?: string): string {
  return `${giveawayUrl(slug, origin)}?${REFERRAL_PARAM}=${userId}`
}

/** The referrer named by a `?ref=` value, or null if it isn't a user id. */
export function parseReferrer(value: string | null | undefined): number | null {
  if (!value || !/^\d{1,18}$/.test(value)) return null
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export function saveReferrer(giveawayId: number, referrerId: number): void {
  try {
    localStorage.setItem(keyFor(giveawayId), String(referrerId))
  } catch {
    /* storage unavailable — the referral is lost if they leave the page */
  }
}

/** Who referred this visitor to the giveaway, never themselves. */
export function loadReferrer(giveawayId: number, selfId?: number | null): number | null {
  try {
    const id = parseReferrer(localStorage.getItem(keyFor(giveawayId)))
    return id !== null && id !== selfId ? id : null
  } catch {
    return null
  }
}

export function clearReferrer(giveawayId: number): void {
  try {
    localStorage.removeItem(keyFor(giveawayId))
  } catch {
    /* nothing to do */
  }
}
